import { fetchWrapper } from '@/_helpers';
import React, { useEffect, useState } from 'react';
import { BsHandThumbsUpFill, BsHandThumbsUp, BsHandThumbsDownFill, BsHandThumbsDown } from 'react-icons/bs';
import { VscLoading } from 'react-icons/vsc';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { css } from 'twin.macro';
import { toast } from 'react-toastify';

const getLikedValue = (fb) => {
    if (!fb) return null;
    if (typeof fb.liked === 'boolean') return fb.liked;
    return null;
};

const normalizeId = (val) => (val || '').toString();

const GPTFeedBackLikeDislike = ({ m, rId, currUser }) => {
    const messageId = m?.id || m?._id;
    const userId = normalizeId(currUser?._id || currUser?.id);

    const existingFeedback = (m?.gptFeedbacks || []).find((fb) => normalizeId(fb.user) === userId);
    const [liked, setLiked] = useState(getLikedValue(existingFeedback));
    const [description, setDescription] = useState(existingFeedback?.description || '');
    const [showModal, setShowModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        setLiked(getLikedValue(existingFeedback));
        setDescription(existingFeedback?.description || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageId]);

    const submitFeedback = async (newLiked, newDescription) => {
        if (isUpdating || !messageId) {
            return;
        }

        const currentlyActive = liked;
        setIsUpdating(true);

        try {
            let endpoint;
            if (rId) {
                endpoint = `chatrooms/${rId}/${messageId}/gpt-feedback`;
            } else {
                throw new Error('Missing chatroom id');
            }

            // toggle off the existing feedback when the same value is selected again
            const valueToSend = currentlyActive === newLiked ? null : newLiked;
            await fetchWrapper.put(endpoint, {
                liked: valueToSend,
                description: newDescription || '',
            });
            setLiked(valueToSend);
            if (valueToSend === false) {
                setDescription(newDescription || '');
            }
        } catch (err) {
            toast.error(err?.message);
            console.error(err?.message || 'Failed to update feedback. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleLike = () => {
        submitFeedback(true, '');
    };

    const handleDislikeClick = () => {
        if (isDisliked) {
            submitFeedback(false, '');
            return;
        }
        setDescription(existingFeedback?.description || '');
        setShowModal(true);
    };

    const handleDislikeSubmit = () => {
        setShowModal(false);
        submitFeedback(false, description);
    };

    const isLiked = liked === true;
    const isDisliked = liked === false;

    return (
        <>
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    margin-bottom: 4px;
                    opacity: ${isUpdating ? 0.25 : 1};
                    pointer-events: ${isUpdating ? 'none' : 'auto'};
                `}
                className="dropper feedback-icon mx-2"
                aria-busy={isUpdating}
                aria-disabled={isUpdating}
            >
                {isUpdating ? (
                    <VscLoading size={25} className="spin" />
                ) : (
                    <>
                        <div
                            onClick={handleLike}
                            css={css`
                                cursor: pointer;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                svg {
                                    color: ${isLiked ? '#50aeb0' : '#6c757d'};
                                    transition: color 0.2s;
                                }
                                :hover svg {
                                    color: #50aeb0cc;
                                }
                            `}
                            title="Like Bob Response"
                        >
                            {isLiked ? <BsHandThumbsUpFill size={25} /> : <BsHandThumbsUp size={25} />}
                        </div>
                        <div
                            onClick={handleDislikeClick}
                            css={css`
                                cursor: pointer;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                svg {
                                    color: ${isDisliked ? '#c93535' : '#6c757d'};
                                    transition: color 0.2s;
                                }
                                :hover svg {
                                    color: #c93535cc;
                                }
                            `}
                            title="Dislike Bob Response"
                        >
                            {isDisliked ? <BsHandThumbsDownFill size={25} /> : <BsHandThumbsDown size={25} />}
                        </div>
                    </>
                )}
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Feedback</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe what was wrong with this response..."
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isUpdating}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleDislikeSubmit}
                        disabled={isUpdating || description.length === 0}
                    >
                        {isUpdating ? <Spinner animation="border" size="sm" /> : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default GPTFeedBackLikeDislike;
